import { useCallback, useEffect, useRef } from 'react';

import { useAgentManager } from '../providers/AgentProvider';
import {
  type FocusNodeListener,
  type HighlightNodeListener,
  type HighlightNodePayload,
  type MessagesChangedListener,
  type StateChangedListener,
  type SubscriptionOptions,
  type ToolCallResultListener,
} from '../types/subscriptions';

/**
 * Hook to subscribe to focus node events.
 * Automatically cleans up subscription on unmount.
 */
export const useFocusNodeSubscription = (callback: FocusNodeListener): void => {
  const { eventSubscriptions } = useAgentManager();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventSubscriptions) {
      return;
    }

    const unsubscribe = eventSubscriptions.subscribeToFocusNode((payload) => callbackRef.current(payload));

    return unsubscribe;
  }, [eventSubscriptions]);
};

/**
 * Returns a stable callback to dispatch a focus-node event.
 */
export const useFocusNode = () => {
  const { eventSubscriptions } = useAgentManager();

  return useCallback(
    (runId: string) => {
      eventSubscriptions?.notifyFocusNode(runId);
    },
    [eventSubscriptions],
  );
};

/**
 * Hook to subscribe to highlight node events.
 * Automatically cleans up subscription on unmount.
 */
export const useHighlightNodeSubscription = (callback: HighlightNodeListener): void => {
  const { eventSubscriptions } = useAgentManager();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventSubscriptions) {
      return;
    }

    const unsubscribe = eventSubscriptions.subscribeToHighlightNode((payload) => callbackRef.current(payload));

    return unsubscribe;
  }, [eventSubscriptions]);
};

/**
 * Returns a stable callback to dispatch a highlight-node event.
 */
export const useHighlightNode = () => {
  const { eventSubscriptions } = useAgentManager();

  return useCallback(
    (payload: HighlightNodePayload) => {
      eventSubscriptions?.notifyHighlightNode(payload);
    },
    [eventSubscriptions],
  );
};

/**
 * Hook to subscribe to tool call result events.
 * Automatically cleans up subscription on unmount.
 *
 * By default, receives events from all threads. Use options.threadId to filter to a specific thread.
 *
 * @param callback - Function called when a tool call result is received. Payload includes threadId.
 * @param options - Optional configuration to filter to a specific threadId
 *
 * @example
 * ```tsx
 * useToolCallResultSubscription((payload) => {
 *   console.log(payload.threadId);
 *   if (payload.toolCallName === 'file_edit_complete') {
 *     // Handle file edit completion
 *   }
 * });
 * ```
 */
export const useToolCallResultSubscription = (
  callback: ToolCallResultListener,
  options?: SubscriptionOptions,
): void => {
  const { eventSubscriptions } = useAgentManager();
  const callbackRef = useRef(callback);
  const threadId = options?.threadId;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventSubscriptions) {
      return;
    }

    const subscriptionOptions = threadId ? { threadId } : undefined;
    const unsubscribe = eventSubscriptions.subscribeToToolCallResult(
      (payload) => callbackRef.current(payload),
      subscriptionOptions,
    );

    return unsubscribe;
  }, [eventSubscriptions, threadId]);
};

/**
 * Hook to subscribe to messages changed events.
 * Automatically cleans up subscription on unmount.
 *
 * By default, receives events from all threads. Use options.threadId to filter to a specific thread.
 *
 * Note: For most use cases, prefer `useMessages()` or `useChatMessages()` which provide
 * reactive access to the current messages. Use this hook only when you need to react
 * to message changes (e.g., side effects).
 *
 * @param callback - Function called when messages change. Payload includes threadId.
 * @param options - Optional configuration to filter to a specific threadId
 */
export const useMessagesChangedSubscription = (
  callback: MessagesChangedListener,
  options?: SubscriptionOptions,
): void => {
  const { eventSubscriptions } = useAgentManager();
  const callbackRef = useRef(callback);
  const threadId = options?.threadId;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventSubscriptions) {
      return;
    }

    const subscriptionOptions = threadId ? { threadId } : undefined;
    const unsubscribe = eventSubscriptions.subscribeToMessagesChanged(
      (payload) => callbackRef.current(payload),
      subscriptionOptions,
    );

    return unsubscribe;
  }, [eventSubscriptions, threadId]);
};

/**
 * Hook to subscribe to state changed events.
 * Automatically cleans up subscription on unmount.
 *
 * By default, receives events from all threads. Use options.threadId to filter to a specific thread.
 *
 * Note: For most use cases, prefer `useChatState()` which provides reactive access
 * to the current state. Use this hook only when you need to react to state changes
 * (e.g., side effects).
 *
 * @param callback - Function called when state changes. Payload includes threadId.
 * @param options - Optional configuration to filter to a specific threadId
 */
export const useStateChangedSubscription = (callback: StateChangedListener, options?: SubscriptionOptions): void => {
  const { eventSubscriptions } = useAgentManager();
  const callbackRef = useRef(callback);
  const threadId = options?.threadId;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!eventSubscriptions) {
      return;
    }

    const subscriptionOptions = threadId ? { threadId } : undefined;
    const unsubscribe = eventSubscriptions.subscribeToStateChanged(
      (payload) => callbackRef.current(payload),
      subscriptionOptions,
    );

    return unsubscribe;
  }, [eventSubscriptions, threadId]);
};
